import { Box, CircularProgress, Typography, Skeleton, Stack } from "@mui/material";

/**
 * LoadingState — Phase 2 reusable component.
 *
 * Two variants:
 *   "spinner" (default) — centered spinner + optional label, for page-level
 *                          or drawer-level loading.
 *   "rows"               — skeleton table rows, for tables that want to show
 *                          a placeholder shape while data is fetching
 *                          instead of a blank/spinner-only table.
 *
 * Not wired into any existing page yet — each page's current loading
 * handling (if any) is preserved as-is until that page's own redesign
 * phase, at which point it can opt into this component.
 *
 * Props:
 *   variant  - "spinner" | "rows" (default: "spinner")
 *   label    - optional text under the spinner (spinner variant only)
 *   rows     - number of skeleton rows to render (rows variant only, default 5)
 *   columns  - number of skeleton columns per row (rows variant only, default 4)
 */
export default function LoadingState({
  variant = "spinner",
  label,
  rows = 5,
  columns = 4,
}) {
  if (variant === "rows") {
    return (
      <Stack spacing={1.25} sx={{ py: 1 }}>
        {Array.from({ length: rows }).map((_, r) => (
          <Stack key={r} direction="row" spacing={2} sx={{ px: 2 }}>
            {Array.from({ length: columns }).map((_, c) => (
              <Skeleton
                key={c}
                variant="text"
                height={22}
                sx={{ flex: c === 0 ? "0 0 60px" : 1 }}
              />
            ))}
          </Stack>
        ))}
      </Stack>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        py: 6,
        gap: 1.5,
      }}
    >
      <CircularProgress size={28} thickness={4} />
      {label && (
        <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
          {label}
        </Typography>
      )}
    </Box>
  );
}
